import { EASING_BEZIER } from 'app/config';
import {
  Box,
  Button,
  Stack,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { ReactComponent as ForwardArrowIcon } from 'common/assets/forward-arrow-icon.svg';
import { ReactComponent as CheckMarkIcon } from 'common/assets/checkmark.svg';
import { ReactComponent as MigrationIconStart } from 'common/assets/jb_blue.svg';
import { ReactComponent as MigrationIconEnd } from 'common/assets/jb_one.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectMigrationStartService,
  selectMigrationUnderwayService,
  selectMigrationCompleteService,
} from 'features/entities/entitiesSelectors';
import { selectOSType } from 'features/session/sessionSelectors';
import { MigrationToolbar } from './MigrationToolbar';
import { jbPalette } from '@el2/theme';
import { resetApp } from 'app/redux/rootReducer';
import { updateDashboardActivityRequest } from 'features/entities/entitiesRequests';
import { ActivityTypes } from 'features/entities/entitiesTypes';
import { MigrationBackground } from './MigrationBackground';

import { ReactComponent as SmallArrows } from './assets/arrows-small.svg';
import { InternalNavigationScreens, ServiceCodes } from '../entities/entitiesTypes';
import { screenToRoutesMap } from '../routes/appRoutes';
import { getMapBridge } from 'features/map';

const stepTitleStyle: SxProps = {
  textTransform: 'uppercase',
  color: jbPalette.primary.royalBlue800,
  fontWeight: 'fontWeightRegular',
  letterSpacing: '0.08rem',
};
const stepActionStyle: SxProps<Theme> = {
  textTransform: 'uppercase',
  fontSize: (theme) => theme.typography.pxToRem(20),
  color: 'primary.main',
  fontWeight: 'fontWeightRegular',
  letterSpacing: '0.04rem',
  lineHeight: '26px',
};

export const MigrationStartView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isTabletWidth = useMediaQuery('(min-width:481px)');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const visibilityListenerRef = useRef<(() => void) | null>(null);

  const migrationStartService = useSelector(selectMigrationStartService);
  const migrationUnderwayService = useSelector(selectMigrationUnderwayService);
  const migrationCompleteService = useSelector(selectMigrationCompleteService);
  const service = migrationStartService ?? migrationUnderwayService ?? migrationCompleteService;
  const osType = useSelector(selectOSType);
  const appUrl = useMemo(() => {
    if (osType === 'Android') {
      return service?.androidUrl;
    } else if (osType === 'iOS') {
      return service?.iosUrl;
    } else {
      return service?.url;
    }
  }, [osType, service?.androidUrl, service?.iosUrl, service?.url]);

  const handleDownload = useCallback(() => {
    window.open(appUrl ?? undefined, '_blank');
    return null;
  }, [appUrl]);

  const handleActivate = useCallback(() => {
    const mapBridge = getMapBridge();
    mapBridge.deleteAllCookies();

    if (!service) return;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        visibilityListenerRef.current = null;
        // Navigate to root before resetting state so that MigrationStartView
        // does not re-render with empty entity state
        navigate('/', { replace: true });
        dispatch(resetApp());
      }
    };

    visibilityListenerRef.current = onVisibilityChange;
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Open external WebView directly (bypassing the epic so we control the return behavior)
    mapBridge.openPage(service.url);

    // Track activity (fire-and-forget, same as the epic does)
    updateDashboardActivityRequest({
      activityType: ActivityTypes.EXTERNAL_NAVIGATION,
      serviceCode: service.code,
    });
  }, [dispatch, navigate, service]);

  // Clean up visibility listener if component unmounts before user returns
  useEffect(() => {
    return () => {
      if (visibilityListenerRef.current) {
        document.removeEventListener('visibilitychange', visibilityListenerRef.current);
      }
    };
  }, []);

  const features = useMemo(
    () => [
      t('migration.feature.list.feature1'),
      t('migration.feature.list.feature2'),
      t('migration.feature.list.feature3'),
    ],
    [t]
  );
  if (!service || service?.code === ServiceCodes.MIGRATION_COMPLETE) {
    return <Navigate to={screenToRoutesMap[InternalNavigationScreens.HOME_SCREEN]} />;
  }

  return (
    <motion.div
      style={{
        width: '100%',
        flex: '1',
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        containerType: 'size',
      }}
      initial={{ x: window.innerWidth }}
      animate={{ x: 0 }}
      exit={{ x: window.innerWidth }}
      transition={{ ease: EASING_BEZIER, duration: 0.2 }}
    >
      <MigrationToolbar />
      <MigrationBackground />
      <Box
        sx={{
          maxWidth: '480px',
          display: 'flex',
          flexDirection: 'column',
          flex: '1',
          p: 3,
          zIndex: 1,
          overflowY: 'auto',
          '@container (min-height: 744px)': {
            pt: 8,
          },
        }}
      >
        <Stack gap={3}>
          <Typography
            variant="h1"
            sx={{
              color: jbPalette.primary.royalBlue900,
              fontSize: (theme) => theme.typography.pxToRem(24),
              lineHeight: '28px',
            }}
          >
            {t('migration.main.title')}
          </Typography>
          <Stack component="ul" gap={1} sx={{ m: 0, p: 0 }}>
            {features.map((feature, index) => (
              <Box
                component="li"
                key={`feature-item-${index}`}
                sx={{
                  display: 'inline-flex',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <CheckMarkIcon />
                <Typography
                  variant="body1"
                  component="div"
                  sx={{ fontWeight: 'fontWeightRegular', flex: 1, lineHeight: '20px' }}
                >
                  {feature}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
        <Box sx={{ pt: 5 }}>
          <Box>
            <Typography variant="body2" component="div" sx={stepTitleStyle}>
              {t('migration.step.download.counter')}
            </Typography>
            <Box sx={{ pt: 0.5 }}>
              <Typography component="div" sx={stepActionStyle}>
                {t('migration.step.download.name')}
              </Typography>
            </Box>
            <Box sx={{ pt: 2 }}>
              <Button
                onClick={handleDownload}
                variant="outlined"
                fullWidth
                sx={{ letterSpacing: '0.08rem' }}
              >
                {t('migration.step.download.button')}
              </Button>
            </Box>
          </Box>
          <Stack sx={{ pt: 6 }} gap={3}>
            <Box>
              <Typography variant="body2" component="div" sx={stepTitleStyle}>
                {t('migration.step.activate.counter')}
              </Typography>
              <Box sx={{ pt: 0.5 }}>
                <Typography component="div" sx={stepActionStyle}>
                  {t('migration.step.activate.name')}
                </Typography>
              </Box>
              <Typography
                component="div"
                sx={{
                  pt: 1,
                  fontWeight: 'fontWeightRegular',
                  lineHeight: '22px',
                }}
              >
                {t('migration.step.activate.description')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'none',
                '@container (min-height: 780px) and (max-width:480px)': {
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 3,
                  alignItems: 'center',
                  height: isTabletWidth ? 178 : 122,
                },
                '@container (min-height: 808px) and (min-width:481px)': {
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 3,
                  alignItems: 'center',
                  height: isTabletWidth ? 178 : 122,
                },
              }}
              className="migrationGraphics"
            >
              <MigrationIconStart {...(isTabletWidth && { width: 92, height: 92 })} />
              <SmallArrows />
              <Box
                sx={{
                  width: isTabletWidth ? 114 : 98,
                  aspectRatio: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <MigrationIconEnd
                  style={{
                    ...(isTabletWidth
                      ? {
                          flex: '0 0 154px',
                          width: 150,
                          height: 150,
                        }
                      : {
                          flex: '0 0 126px',
                        }),
                  }}
                />
              </Box>
            </Box>

            <Button
              onClick={handleActivate}
              variant="contained"
              fullWidth
              sx={{ letterSpacing: '0.08rem' }}
              endIcon={<ForwardArrowIcon style={{ height: 16 }} />}
            >
              {t('migration.step.activate.button')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </motion.div>
  );
};
